import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import { Box, FormControlLabel, Grid, IconButton, Switch, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Notification from "../../../components/Common/Notification";
import CustomButton from "../../../components/Form/CustomButton";
import CustomTextField from "../../../components/Form/CustomTextField";
import { getNavigationMenu, updateNavigationMenu } from "../../../store/setting/settingApi";
import "../setting.css";

const newItem = () => ({
    title: "",
    url: "/",
    status: 1,
    sort_order: 0,
    children: [],
});

const MenuItemEditor = ({ item, depth, editable, onChange, onRemove, onAddChild, onMove }) => (
    <Box
        sx={{
            border: "1px solid #EDEDED",
            borderRadius: 1,
            p: 2,
            mb: 1,
            ml: depth * 3,
            background: "#fff",
        }}
    >
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
                <CustomTextField
                    title="Menu label"
                    values={item.title}
                    onChange={(v) => onChange({ ...item, title: v })}
                    disabled={!editable}
                />
            </Grid>
            <Grid item xs={12} md={5}>
                <CustomTextField
                    title="URL"
                    values={item.url}
                    onChange={(v) => onChange({ ...item, url: v })}
                    disabled={!editable}
                    placeholder="/ or https://..."
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={item.status === 1}
                            onChange={(e) => onChange({ ...item, status: e.target.checked ? 1 : 0 })}
                            disabled={!editable}
                        />
                    }
                    label="Enabled"
                />
            </Grid>
        </Grid>
        {editable && (
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                <IconButton size="small" onClick={() => onMove(-1)} title="Move up">
                    <Icon icon="ri-arrow-up-line" />
                </IconButton>
                <IconButton size="small" onClick={() => onMove(1)} title="Move down">
                    <Icon icon="ri-arrow-down-line" />
                </IconButton>
                <CustomButton
                    title="Add sub-item"
                    press={onAddChild}
                    backgroundColor="white"
                    textColor="#101010"
                    borderStyle="1px solid #E3E8EE"
                />
                <CustomButton
                    title="Remove"
                    press={onRemove}
                    backgroundColor="white"
                    textColor="#ED1A3D"
                    borderStyle="1px solid #E3E8EE"
                />
            </Box>
        )}
        {(item.children || []).map((child, childIndex) => (
            <MenuItemEditor
                key={`${depth}-${childIndex}`}
                item={child}
                depth={depth + 1}
                editable={editable}
                onChange={(updated) => {
                    const children = [...(item.children || [])];
                    children[childIndex] = updated;
                    onChange({ ...item, children });
                }}
                onRemove={() => {
                    const children = (item.children || []).filter((_, i) => i !== childIndex);
                    onChange({ ...item, children });
                }}
                onAddChild={() => {
                    const children = [...(item.children || []), newItem()];
                    onChange({ ...item, children });
                }}
                onMove={(dir) => {
                    const children = [...(item.children || [])];
                    const target = childIndex + dir;
                    if (target < 0 || target >= children.length) return;
                    [children[childIndex], children[target]] = [children[target], children[childIndex]];
                    onChange({ ...item, children });
                }}
            />
        ))}
    </Box>
);

const NavigationMenu = ({ userPermission }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editable, setEditable] = useState(false);
    const [items, setItems] = useState([]);
    const [notification, setNotification] = useState({ open: false, msg: "", type: "success" });

    useEffect(() => {
        if (userPermission?.length > 0) {
            userPermission.includes("edit-setting") && setEditable(true);
            !userPermission.includes("view-setting") && navigate("/not-authorized");
        }
    }, [userPermission, navigate]);

    const loadMenu = async () => {
        setLoading(true);
        const response = await getNavigationMenu();
        if (response?.code === "success") {
            setItems(response?.data?.tree?.length ? response.data.tree : [newItem(), newItem()]);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadMenu();
    }, []);

    const updateItem = (index, updated) => {
        setItems((prev) => prev.map((item, i) => (i === index ? updated : item)));
    };

    const moveItem = (index, dir) => {
        setItems((prev) => {
            const next = [...prev];
            const target = index + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleSave = async () => {
        if (!editable) return;
        setSaving(true);
        const response = await updateNavigationMenu({ items });
        setNotification({
            open: true,
            msg: response?.message || response?.warning || (response?.code === "success" ? "Saved." : "Save failed."),
            type: response?.code === "success" ? "success" : "error",
        });
        if (response?.code === "success") {
            setItems(response?.data?.tree || items);
        }
        setSaving(false);
    };

    return (
        <div className="page-content">
            <LoadingDialog open={loading || saving} />
            <Container fluid>
                <Notification notification={notification} setNotification={setNotification} />
                <Breadcrumb
                    title="Menu"
                    backRoute="/settings"
                    breadcrumbItems={[
                        { title: "Settings", link: "/settings" },
                        { title: "Menu", link: "/settings-navigation-menu" },
                    ]}
                />
                <div className="setting-desc mb-3">
                    Drag-style ordering via move up/down. <strong>SHOP</strong> mega-menu is fixed on the storefront and cannot be edited here.
                </div>
                <Card>
                    <CardBody>
                        {items.map((item, index) => (
                            <MenuItemEditor
                                key={index}
                                item={item}
                                depth={0}
                                editable={editable}
                                onChange={(updated) => updateItem(index, updated)}
                                onRemove={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                                onAddChild={() =>
                                    updateItem(index, {
                                        ...item,
                                        children: [...(item.children || []), newItem()],
                                    })
                                }
                                onMove={(dir) => moveItem(index, dir)}
                            />
                        ))}
                        {editable && (
                            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                                <CustomButton
                                    title="Add menu item"
                                    press={() => setItems((prev) => [...prev, newItem()])}
                                    backgroundColor="white"
                                    textColor="#101010"
                                    borderStyle="1px solid #E3E8EE"
                                />
                                <CustomButton
                                    title="Save menu"
                                    press={handleSave}
                                    backgroundColor="#101010"
                                    textColor="#FFFFFF"
                                    borderStyle="1px solid #101010"
                                />
                            </Box>
                        )}
                        <Typography variant="caption" display="block" sx={{ mt: 2, color: "#666" }}>
                            Default top-level items: HOME (/) and SALE (/sale). SHOP appears automatically after the first menu item.
                        </Typography>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

const mapStateToProps = ({ Login }) => ({
    userPermission: Login.permissionArry,
});

export default connect(mapStateToProps)(NavigationMenu);
